import { useEffect, useRef } from 'react';

/**
 * HeroBackground (project100 T1008 / D-009) — the signature animated backdrop
 * behind the landing hero. A drifting node/pulse motif evoking the Raisy
 * engine: Entity → Process → Bot → Kernel (four node sizes, pulses traveling
 * the edges like work moving through the graph).
 *
 * Perf budget: pure canvas 2D, no WebGL, no deps. Pauses when the tab is
 * hidden, renders one static frame under prefers-reduced-motion, caps DPR at
 * 2, and follows the light/dark theme via the `dark` class on <html>.
 */

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** 0=Entity (largest) … 3=Kernel (smallest) */
  layer: 0 | 1 | 2 | 3;
}

interface Pulse {
  from: number;
  to: number;
  /** 0→1 progress along the edge */
  t: number;
  speed: number;
  orange: boolean;
}

const LAYER_RADIUS = [3.4, 2.8, 2.2, 1.6];
const NODE_COUNT = 42;
const LINK_DIST = 140;
const PULSE_SPAWN_EVERY_MS = 1400;

const CYAN = { r: 34, g: 211, b: 238 };
const TEAL = { r: 45, g: 212, b: 191 };
const ORANGE = { r: 253, g: 128, b: 37 };

const rgba = (c: { r: number; g: number; b: number }, a: number) =>
  `rgba(${c.r},${c.g},${c.b},${a})`;

/**
 * The run's own FINISHED UNITS, as the doorway's widget already receives them — @P100 D-031 option B
 * (*"the backdrop IS the run"*), fed by D-029's stream of one event per real unit of work.
 *
 * `units` is COUNTED from the engine's events and never derived here: the canvas lights one node per
 * finished unit, so the lit set is the run's real progress and nothing else. `null` means no run on
 * this page — the field stays the near-silent motif it has always been, which is D-031's own hard
 * constraint (silent at idle), not a fallback we invented.
 */
export interface HeroRunSignal {
  /** Units the engine has finished (`company`, `ranked`, one per `draft`). */
  units: number;
  /** True while the run is in flight; false once it finished — the lit set then HOLDS, because
   *  effort that has happened must not un-happen on screen (persona: evidence accumulating). */
  running: boolean;
}

/** One lit node per finished unit; the doorway's own run is 7 units, so this is headroom, not a cap. */
const MAX_LIT = 12;

export const HeroBackground = ({ run }: { run?: HeroRunSignal | null }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  /** Read by the rAF loop, so a run update never re-mounts the canvas or restarts its motif. */
  const runRef = useRef<HeroRunSignal | null>(run ?? null);
  /** How many nodes the run has earned. The rAF loop and the repaint below both read this. */
  const litRef = useRef(0);
  /** Under prefers-reduced-motion there is no loop, so a prop change repaints through this. */
  const repaintRef = useRef<() => void>(() => {});

  useEffect(() => {
    runRef.current = run ?? null;
    litRef.current = run ? Math.max(0, Math.min(run.units, MAX_LIT)) : 0;
    repaintRef.current();
  }, [run]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let dark = document.documentElement.classList.contains('dark');
    const themeObserver = new MutationObserver(() => {
      dark = document.documentElement.classList.contains('dark');
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    let width = 0;
    let height = 0;
    let nodes: Node[] = [];
    let pulses: Pulse[] = [];
    let rafId = 0;
    let lastPulseAt = 0;
    /** The lit count the last frame drew, so a NEW finished unit emits exactly one arrival pulse. */
    let lastLit = 0;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const seed = () => {
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        layer: (Math.floor(Math.random() * 4) as Node['layer']),
      }));
      pulses = [];
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();

      // Pre-layout (or a hidden section) the box is 0×0. Sizing to that would paint a
      // blank frame and seed every node at the origin; wait for a real box instead.
      if (rect.width < 1 || rect.height < 1) return;

      const prevW = width;
      const prevH = height;
      width = rect.width;
      height = rect.height;

      const bw = Math.round(width * dpr);
      const bh = Math.round(height * dpr);
      // Assigning width/height clears the bitmap, so only touch them when the size
      // actually moved — that keeps an observer echo from blanking a frame.
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }

      if (nodes.length === 0) {
        seed();
      } else if (prevW > 0 && prevH > 0) {
        // Carry the motif across the new box rather than leaving it bunched in a corner.
        const kx = width / prevW;
        const ky = height / prevH;
        for (const n of nodes) {
          n.x *= kx;
          n.y *= ky;
        }
      }

      // With prefers-reduced-motion there is no rAF loop, so the repaint has to
      // happen here or a resize would leave the canvas blank.
      if (reducedMotion.matches) drawFrame();
    };

    const drawFrame = () => {
      ctx.clearRect(0, 0, width, height);

      // Softer in light mode so first paint stays readable.
      const edgeAlpha = dark ? 0.16 : 0.1;
      const nodeAlpha = dark ? 0.7 : 0.45;

      // Edges
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.hypot(dx, dy);
          if (dist < LINK_DIST) {
            const a = edgeAlpha * (1 - dist / LINK_DIST);
            ctx.strokeStyle = rgba(i % 2 === 0 ? CYAN : TEAL, a);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      // Nodes — color by layer: Entity/Process teal, Bot/Kernel cyan.
      // THE RUN LIVES HERE (D-031 option B): the first `litRef.current` nodes are the units the engine
      // has FINISHED, so the lit set IS the progress — counted from its events, never a fraction this
      // canvas derived. At idle the count is 0 and this loop draws what it always drew.
      const lit = litRef.current;
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const c = n.layer < 2 ? TEAL : CYAN;
        if (i < lit) {
          // A finished unit: brighter, with the glow ring that makes it read as work that happened.
          ctx.fillStyle = rgba(c, dark ? 0.95 : 0.75);
          ctx.beginPath();
          ctx.arc(n.x, n.y, LAYER_RADIUS[n.layer], 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = rgba(c, dark ? 0.2 : 0.14);
          ctx.beginPath();
          ctx.arc(n.x, n.y, LAYER_RADIUS[n.layer] + 7, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = rgba(c, nodeAlpha);
          ctx.beginPath();
          ctx.arc(n.x, n.y, LAYER_RADIUS[n.layer], 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Pulses — work traveling the graph; the rare orange one is the glow accent
      pulses = pulses.filter((p) => p.t <= 1);
      for (const p of pulses) {
        const a = nodes[p.from];
        const b = nodes[p.to];
        const x = a.x + (b.x - a.x) * p.t;
        const y = a.y + (b.y - a.y) * p.t;
        const c = p.orange ? ORANGE : CYAN;
        const alpha = dark ? 0.95 : 0.7;
        ctx.fillStyle = rgba(c, alpha);
        ctx.beginPath();
        ctx.arc(x, y, p.orange ? 3 : 2.2, 0, Math.PI * 2);
        ctx.fill();
        // Glow
        ctx.fillStyle = rgba(c, alpha * 0.25);
        ctx.beginPath();
        ctx.arc(x, y, p.orange ? 9 : 7, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = (now: number) => {
      // Drift
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < -10) n.x = width + 10;
        if (n.x > width + 10) n.x = -10;
        if (n.y < -10) n.y = height + 10;
        if (n.y > height + 10) n.y = -10;
      }

      // Advance + spawn pulses along real edges
      for (const p of pulses) p.t += p.speed;

      // A FINISHED UNIT ARRIVES AS A PULSE (D-031 B) — and the event stream is the ONLY thing allowed
      // to emit one. No unit, no pulse: a backdrop that invents activity on the first screen fails
      // D-025's SOURCE/ACCURACY gate, which is the gate that made B defensible in the first place.
      const litNow = litRef.current;
      if (litNow > lastLit) {
        const to = Math.min(litNow - 1, nodes.length - 1);
        for (let tries = 0; tries < 8; tries++) {
          const from = Math.floor(Math.random() * nodes.length);
          if (from === to) continue;
          const dist = Math.hypot(nodes[from].x - nodes[to].x, nodes[from].y - nodes[to].y);
          if (dist < LINK_DIST * 1.6) {
            pulses.push({ from, to, t: 0, speed: 0.028, orange: false });
            break;
          }
        }
        lastLit = litNow;
      } else if (litNow < lastLit) {
        // The run was cleared (fresh page, reset): the field goes quiet with it, earned nodes and all.
        lastLit = litNow;
      }
      if (now - lastPulseAt > PULSE_SPAWN_EVERY_MS && pulses.length < 4) {
        // find an actual edge to travel
        for (let tries = 0; tries < 8; tries++) {
          const i = Math.floor(Math.random() * nodes.length);
          const j = Math.floor(Math.random() * nodes.length);
          if (i === j) continue;
          const dist = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
          if (dist < LINK_DIST) {
            pulses.push({
              from: i,
              to: j,
              t: 0,
              speed: 0.012 + Math.random() * 0.01,
              orange: Math.random() < 0.2,
            });
            lastPulseAt = now;
            break;
          }
        }
      }

      drawFrame();
      rafId = requestAnimationFrame(step);
    };

    const start = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(step);
    };
    const stop = () => cancelAnimationFrame(rafId);

    const onVisibility = () => {
      if (reducedMotion.matches) return;
      if (document.hidden) stop();
      else start();
    };

    // The hero's height is content-driven and settles AFTER this mount (the guest
    // widget, the promo banner, webfont swap). Sizing the bitmap once and never
    // re-measuring leaves it at the mount-time size while the box grows, so every
    // circle is drawn round and then stretched into a vertical ellipse by CSS —
    // measured at 10% on a cold load of the landing page. The observer is the fix.
    const sizeObserver = new ResizeObserver(() => resize());
    sizeObserver.observe(canvas);

    repaintRef.current = drawFrame;
    resize();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);

    if (reducedMotion.matches) {
      // Static signature: one honest frame, no motion.
      drawFrame();
    } else {
      start();
    }

    return () => {
      stop();
      repaintRef.current = () => {};
      themeObserver.disconnect();
      sizeObserver.disconnect();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
};
